import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Banknote, CreditCard as CardIcon, Smartphone, DollarSign, Trash2, Lock, Printer, ArrowDown, ArrowUp, AlertTriangle, MessageCircle, FileText, Download, Building2, Eye, EyeOff } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

const METHOD_META = {
  cash: { label: "Dinheiro", color: "#10B981", icon: Banknote },
  card: { label: "Cartão", color: "#3B82F6", icon: CardIcon },
  pix: { label: "Pix", color: "#E11D48", icon: Smartphone },
};

export default function DailyCash() {
  const { user } = useAuth();
  const [date, setDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [data, setData] = useState(null);
  const [mvOpen, setMvOpen] = useState(false);
  const [mvType, setMvType] = useState("sangria");
  const [mvForm, setMvForm] = useState({ amount: "", reason: "" });
  const [delOpen, setDelOpen] = useState(false);
  const [delSale, setDelSale] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [delLoading, setDelLoading] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeForm, setCloseForm] = useState({ counted_cash: "", leftover_cash: "0", notes: "" });
  const [closing, setClosing] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportPeriod, setReportPeriod] = useState("month");
  const [reportDate, setReportDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("all"); // admin: 'all' | <unit_id>
  const [adminPeek, setAdminPeek] = useState(false); // admin: ver vendas de caixa fechado sem reabrir
  const isAdmin = user?.role === "admin";
  const perms = user?.permissions || [];
  const canClose = isAdmin || user?.role === "finance" || perms.includes("cash.open_close");
  const canDelete = isAdmin || user?.role === "finance" || perms.includes("sales.delete");
  const canReopen = isAdmin || user?.role === "finance" || perms.includes("cash.open_close");

  // Para admin, "all" significa agregado de todas as unidades; para ações (fechar/reabrir/sangria/suprimento)
  // é obrigatório selecionar uma unidade específica.
  const scopeUnit = isAdmin ? (selectedUnit !== "all" ? selectedUnit : null) : (user?.unit_id || null);
  const needsUnitForAction = isAdmin && selectedUnit === "all";

  const load = async () => {
    try {
      const params = { date };
      if (isAdmin && selectedUnit !== "all") params.unit_id = selectedUnit;
      setData((await api.get(`/finance/daily-cash`, { params })).data);
    }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  useEffect(() => { load(); setAdminPeek(false); }, [date, selectedUnit]);
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try { setUnits((await api.get("/units")).data || []); } catch { /* ignore */ }
    })();
  }, [isAdmin]);

  const openDelete = (sale) => {
    setDelSale(sale);
    setDelReason("");
    setDelOpen(true);
  };
  const confirmDelete = async () => {
    if (!delReason.trim()) return toast.error("Informe o motivo da exclusão");
    setDelLoading(true);
    try {
      const q = new URLSearchParams({ reason: delReason.trim() }).toString();
      const { data: r } = await api.delete(`/sales/plan/${delSale.session_id}?${q}`);
      toast.success(r.still_active ? "Venda removida — aluno segue ativo com outro plano" : "Venda removida — dias deste plano revertidos");
      setDelOpen(false); setDelSale(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setDelLoading(false); }
  };

  const openMv = (t) => {
    if (needsUnitForAction) return toast.error("Selecione uma unidade específica no topo antes de registrar movimentos");
    setMvType(t); setMvForm({ amount: "", reason: "" }); setMvOpen(true);
  };
  const saveMv = async () => {
    if (!mvForm.amount || !mvForm.reason.trim()) return toast.error("Preencha valor e motivo");
    try {
      const params = {};
      if (isAdmin && scopeUnit) params.unit_id = scopeUnit;
      await api.post("/finance/cash-movement", { type: mvType, amount: parseFloat(mvForm.amount), reason: mvForm.reason }, { params });
      toast.success(mvType === "sangria" ? "Sangria registrada" : "Suprimento registrado");
      setMvOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const openCloseDialog = () => {
    if (needsUnitForAction) return toast.error("Selecione uma unidade específica no topo antes de fechar o caixa");
    const expected = (data?.opening_balance || 0) + (data?.by_method?.cash || 0);
    setCloseForm({ counted_cash: expected.toFixed(2), leftover_cash: "0", notes: "" });
    setCloseOpen(true);
  };
  const closeDay = async () => {
    setClosing(true);
    try {
      const qObj = {
        date,
        counted_cash: parseFloat(closeForm.counted_cash || 0).toString(),
        leftover_cash: parseFloat(closeForm.leftover_cash || 0).toString(),
        notes: closeForm.notes || "",
      };
      if (isAdmin && scopeUnit) qObj.unit_id = scopeUnit;
      const q = new URLSearchParams(qObj).toString();
      const { data: r } = await api.post(`/finance/close-day?${q}`);
      if (r.values_hidden) { toast.success("Caixa fechado. Os valores ficam visíveis apenas para o administrador."); setCloseOpen(false); load(); return; }
      const diff = r.difference;
      if (Math.abs(diff) < 0.01) toast.success(`Caixa fechado — bateu certinho (R$ ${r.expected_cash.toFixed(2)})`);
      else if (diff > 0) toast.info(`Caixa fechado com sobra de R$ ${diff.toFixed(2)}`);
      else toast.error(`Caixa fechado com falta de R$ ${Math.abs(diff).toFixed(2)}`);
      setCloseOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setClosing(false); }
  };

  const openReport = () => { setReportData(null); setReportOpen(true); loadReport(); };
  const loadReport = async () => {
    setReportLoading(true);
    try {
      const { data } = await api.get(`/finance/closures-report`, { params: { period: reportPeriod, date: reportDate } });
      setReportData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao carregar relatório"); }
    finally { setReportLoading(false); }
  };
  useEffect(() => { if (reportOpen) loadReport(); /* eslint-disable-next-line */ }, [reportPeriod, reportDate]);
  const exportReportCSV = () => {
    if (!reportData?.closures?.length) return toast.error("Nenhum fechamento no período");
    const rows = [["Data","Unidade","Fechado por","Dinheiro","Cartão","Pix","Total","Esperado","Contado","Diferença","Vendas","Fechado em"]];
    reportData.closures.forEach(c => rows.push([
      c.date, c.unit_name || "—", c.closed_by_name || "—",
      (c.cash||0).toFixed(2), (c.card||0).toFixed(2), (c.pix||0).toFixed(2),
      (c.total||0).toFixed(2), (c.expected_cash||0).toFixed(2), (c.counted_cash||0).toFixed(2),
      (c.difference||0).toFixed(2), c.count||0, new Date(c.closed_at).toLocaleString("pt-BR"),
    ]));
    const csv = rows.map(r => r.map(f => `"${String(f).replace(/"/g,'""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `fechamentos_${reportData.period}_${reportData.anchor}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("CSV baixado");
  };
  const printReport = () => {
    if (!reportData) return;
    const r = reportData;
    const rows = r.closures.map(c => `<tr>
      <td>${c.date}</td><td>${c.unit_name||"—"}</td><td>${c.closed_by_name||"—"}</td>
      <td class="r">R$ ${(c.cash||0).toFixed(2)}</td><td class="r">R$ ${(c.card||0).toFixed(2)}</td>
      <td class="r">R$ ${(c.pix||0).toFixed(2)}</td><td class="r"><strong>R$ ${(c.total||0).toFixed(2)}</strong></td>
      <td class="r ${(c.difference||0)<0?'neg':(c.difference||0)>0?'pos':''}">R$ ${(c.difference||0).toFixed(2)}</td>
    </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Relatório ${r.label}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; color: #0f172a; }
        h1 { color: #e11d48; margin: 0 0 4px; } .sub { color: #64748b; margin-bottom: 18px; font-size: 12px; }
        .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 18px; }
        .kpi { padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; }
        .kpi .l { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; font-weight: bold; }
        .kpi .v { font-size: 20px; font-weight: 900; color: #0f172a; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; }
        th { background: #0f172a; color: white; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; }
        td.r { text-align: right; font-variant-numeric: tabular-nums; }
        tr:nth-child(even) { background: #f8fafc; }
        .pos { color: #0284c7; } .neg { color: #e11d48; }
        .footer { margin-top: 20px; font-size: 10px; color: #94a3b8; text-align: center; }
        @media print { body { padding: 10px; } }
      </style></head><body>
      <h1>Relatório de Fechamentos — ${r.label}</h1>
      <div class="sub">Gerado em ${new Date().toLocaleString("pt-BR")} · ${r.totals.closures_count} fechamento(s) · ${r.totals.sales_tickets} venda(s)</div>
      <div class="kpis">
        <div class="kpi"><div class="l">Total Vendido</div><div class="v">R$ ${r.totals.total_sales.toFixed(2)}</div></div>
        <div class="kpi"><div class="l">Dinheiro</div><div class="v">R$ ${r.totals.cash.toFixed(2)}</div></div>
        <div class="kpi"><div class="l">Cartão</div><div class="v">R$ ${r.totals.card.toFixed(2)}</div></div>
        <div class="kpi"><div class="l">Pix</div><div class="v">R$ ${r.totals.pix.toFixed(2)}</div></div>
      </div>
      <table><thead><tr>
        <th>Data</th><th>Unidade</th><th>Fechado por</th>
        <th class="r">Dinheiro</th><th class="r">Cartão</th><th class="r">Pix</th>
        <th class="r">Total</th><th class="r">Diferença</th>
      </tr></thead><tbody>${rows}</tbody></table>
      <div class="footer">American Fit · ${r.label}</div>
      <script>window.onload=()=>setTimeout(()=>window.print(),400);</script>
    </body></html>`;
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) return toast.error("Popup bloqueado");
    w.document.write(html); w.document.close();
  };
  const printClosure = () => {
    const c = data.closure; if (!c) return;
    const bm = c.by_method || {};
    const diff = c.difference || 0;
    const diffColor = Math.abs(diff) < 0.01 ? "#10B981" : diff > 0 ? "#0EA5E9" : "#E11D48";
    const diffLabel = Math.abs(diff) < 0.01 ? "✓ Confere" : diff > 0 ? "SOBRA" : "FALTA";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Fechamento ${c.date}</title>
    <style>
      body { font-family: -apple-system, 'Segoe UI', system-ui, sans-serif; color: #0F172A; padding: 32px; max-width: 640px; margin: 0 auto; }
      .header { border-bottom: 4px solid #E11D48; padding-bottom: 12px; text-align: center; margin-bottom: 20px; }
      .brand { font-size: 11px; letter-spacing: 2px; text-transform: uppercase; color: #E11D48; font-weight: 900; }
      h1 { margin: 4px 0 0; font-size: 22px; text-transform: uppercase; }
      .meta { color: #64748B; font-size: 12px; margin-top: 4px; }
      .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 16px 0; }
      .box { border: 1px solid #E2E8F0; border-radius: 8px; padding: 12px; }
      .box strong { display: block; color: #64748B; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
      .box .v { font-size: 20px; font-weight: 900; color: #0F172A; font-family: monospace; }
      .methods { border: 1px solid #E2E8F0; border-radius: 8px; padding: 12px; margin: 16px 0; }
      .methods h3 { margin: 0 0 8px; font-size: 12px; text-transform: uppercase; color: #64748B; letter-spacing: 1px; }
      .row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #E2E8F0; font-size: 13px; }
      .row:last-child { border-bottom: none; }
      .row strong { font-weight: 700; }
      .diff { background: ${diffColor}; color: white; padding: 20px; border-radius: 10px; text-align: center; margin: 16px 0; }
      .diff-label { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; opacity: 0.9; }
      .diff-value { font-size: 40px; font-weight: 900; margin-top: 4px; font-family: monospace; }
      .notes { background: #FEF3C7; border: 1px solid #FDE68A; border-radius: 8px; padding: 12px; margin: 12px 0; font-size: 13px; }
      .notes strong { display: block; color: #92400E; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
      .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 60px; text-align: center; }
      .sig .line { border-top: 1px solid #0F172A; padding-top: 4px; font-size: 10px; color: #64748B; text-transform: uppercase; }
      .footer { text-align: center; margin-top: 30px; color: #94A3B8; font-size: 10px; }
      @media print { body { padding: 12px; } }
    </style></head><body>
      <div class="header">
        <div class="brand">American Fit</div>
        <h1>Fechamento de Caixa</h1>
        <div class="meta">${new Date(c.date + "T00:00").toLocaleDateString("pt-BR")} · Fechado em ${new Date(c.closed_at).toLocaleString("pt-BR")} por ${c.closed_by_name}</div>
      </div>
      <div class="grid">
        <div class="box"><strong>Total do dia</strong><div class="v">R$ ${(c.total||0).toFixed(2)}</div></div>
        <div class="box"><strong>Nº de lançamentos</strong><div class="v">${c.count||0}</div></div>
        <div class="box"><strong>Esperado (dinheiro)</strong><div class="v" style="color:#10B981">R$ ${(c.expected_cash||0).toFixed(2)}</div></div>
        <div class="box"><strong>Contado na gaveta</strong><div class="v">R$ ${(c.counted_cash||0).toFixed(2)}</div></div>
        <div class="box"><strong>Saldo p/ amanhã</strong><div class="v">R$ ${(c.leftover_cash||0).toFixed(2)}</div></div>
        <div class="box"><strong>Diferença</strong><div class="v" style="color:${diffColor}">${diff>0?"+":""}R$ ${diff.toFixed(2)}</div></div>
      </div>
      <div class="methods">
        <h3>Recebido por método</h3>
        <div class="row"><span>💵 Dinheiro</span><strong>R$ ${(bm.cash||0).toFixed(2)}</strong></div>
        <div class="row"><span>💳 Cartão</span><strong>R$ ${(bm.card||0).toFixed(2)}</strong></div>
        <div class="row"><span>📱 Pix</span><strong>R$ ${(bm.pix||0).toFixed(2)}</strong></div>
      </div>
      <div class="diff"><div class="diff-label">${diffLabel}</div><div class="diff-value">${diff>0?"+":""}R$ ${diff.toFixed(2)}</div></div>
      ${c.notes ? `<div class="notes"><strong>Observação</strong>${c.notes}</div>` : ""}
      <div class="signatures">
        <div class="sig"><br/><br/><br/><div class="line">${c.closed_by_name} — Responsável do caixa</div></div>
        <div class="sig"><br/><br/><br/><div class="line">Supervisor / Admin</div></div>
      </div>
      <div class="footer">American Fit · Documento emitido em ${new Date().toLocaleString("pt-BR")}</div>
      <script>window.onload=()=>setTimeout(()=>window.print(),400);</script>
    </body></html>`;
    const w = window.open("", "_blank", "width=700,height=850");
    if (!w) return toast.error("Popup bloqueado pelo navegador");
    w.document.write(html); w.document.close();
  };

  const sendClosureWhatsApp = async () => {
    if (!data?.closure) return;
    let cfg;
    try { cfg = (await api.get("/admin-config")).data; } catch { cfg = {}; }
    const phone = (cfg.admin_phone || "").replace(/\D/g, "");
    if (!phone) return toast.error("Cadastre o WhatsApp do admin em Configurações → Configuração do Admin");
    const c = data.closure;
    const bm = c.by_method || {};
    const diff = c.difference || 0;
    const diffLbl = Math.abs(diff) < 0.01 ? "✅ Confere certinho" : diff > 0 ? `🟦 Sobra: R$ ${diff.toFixed(2)}` : `🟥 Falta: R$ ${Math.abs(diff).toFixed(2)}`;
    const salesLines = (data.sales || [])
      .map(x => `• ${new Date(x.created_at).toLocaleTimeString("pt-BR", {hour:"2-digit", minute:"2-digit"})} — ${x.student_name} — ${x.plan_name} — R$ ${x.amount.toFixed(2)} (${METHOD_META[x.method]?.label || x.method})`)
      .join("%0A") || "• Sem vendas de plano no dia";
    const msg =
`🏋️ *American Fit — Fechamento de Caixa*%0A%0A` +
`📅 *Data:* ${new Date(c.date + "T00:00").toLocaleDateString("pt-BR")}%0A` +
`🔒 *Fechado em:* ${new Date(c.closed_at).toLocaleString("pt-BR")}%0A` +
`👤 *Por:* ${c.closed_by_name}%0A%0A` +
`💰 *Total do dia:* R$ ${(c.total||0).toFixed(2)} (${c.count||0} venda(s))%0A%0A` +
`*Recebido por método:*%0A` +
`• 💵 Dinheiro: R$ ${(bm.cash||0).toFixed(2)}%0A` +
`• 💳 Cartão: R$ ${(bm.card||0).toFixed(2)}%0A` +
`• 📱 Pix: R$ ${(bm.pix||0).toFixed(2)}%0A%0A` +
`*Gaveta (dinheiro):*%0A` +
`• Esperado: R$ ${(c.expected_cash||0).toFixed(2)}%0A` +
`• Contado: R$ ${(c.counted_cash||0).toFixed(2)}%0A` +
`• Sobra p/ amanhã: R$ ${(c.leftover_cash||0).toFixed(2)}%0A` +
`• ${diffLbl}%0A%0A` +
`*Planos vendidos:*%0A${salesLines}` +
(c.notes ? `%0A%0A📝 *Obs:* ${c.notes}` : "");
    window.open(`https://wa.me/55${phone}?text=${msg}`, "_blank");
  };

  const printThermalClosure = () => {
    const c = data.closure; if (!c) return;
    const bm = c.by_method || {};
    const diff = c.difference || 0;
    const diffLbl = Math.abs(diff) < 0.01 ? "CONFERE" : diff > 0 ? "SOBRA" : "FALTA";
    const salesLines = (data.sales || [])
      .map(x => `<div class="row"><span class="k">${new Date(x.created_at).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})} ${(x.student_name || "").slice(0, 14)}</span><span class="v">R$ ${x.amount.toFixed(2)}</span></div>`)
      .join("") || `<div class="center sub">Sem vendas de plano</div>`;
    const mvLines = (data.movements || [])
      .map(m => `<div class="row"><span class="k">${m.type === "sangria" ? "▼" : "▲"} ${(m.reason || "").slice(0, 18)}</span><span class="v">${m.type === "sangria" ? "-" : "+"}R$ ${m.amount.toFixed(2)}</span></div>`)
      .join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Fechamento ${c.date}</title>
    <style>
      @page { size: 80mm auto; margin: 0; }
      * { box-sizing: border-box; }
      body { font-family: 'Courier New', monospace; width: 80mm; margin: 0; padding: 6mm 4mm; color: #000; font-size: 11px; line-height: 1.35; }
      .center { text-align: center; }
      .brand { font-size: 14px; font-weight: 900; letter-spacing: 1px; }
      .sub { font-size: 10px; }
      .divider { border-top: 1px dashed #000; margin: 6px 0; }
      .row { display: flex; justify-content: space-between; gap: 6px; font-size: 11px; }
      .row .k { text-transform: uppercase; }
      .row .v { font-weight: 700; text-align: right; }
      .title { font-size: 12px; font-weight: 900; text-transform: uppercase; margin: 4px 0; }
      .total { text-align: center; margin: 6px 0; }
      .total .lbl { font-size: 10px; text-transform: uppercase; }
      .total .val { font-size: 20px; font-weight: 900; }
      .diff { text-align: center; border: 2px solid #000; padding: 6px; margin: 6px 0; }
      .diff .lbl { font-size: 10px; font-weight: 900; text-transform: uppercase; }
      .diff .val { font-size: 16px; font-weight: 900; }
      .sig { border-top: 1px dashed #000; margin-top: 20px; padding-top: 4px; text-align: center; font-size: 9px; text-transform: uppercase; }
      @media print { body { padding: 4mm 3mm; } }
    </style></head><body>
      <div class="center">
        <div class="brand">AMERICAN FIT</div>
        <div class="sub">FECHAMENTO DE CAIXA</div>
        <div class="sub">${new Date(c.date + "T00:00").toLocaleDateString("pt-BR")}</div>
        <div class="sub">${new Date(c.closed_at).toLocaleString("pt-BR")}</div>
        <div class="sub">Por: ${c.closed_by_name}</div>
      </div>
      <div class="divider"></div>
      <div class="total">
        <div class="lbl">Total do dia</div>
        <div class="val">R$ ${(c.total||0).toFixed(2)}</div>
        <div class="sub">${c.count||0} lançamento(s)</div>
      </div>
      <div class="divider"></div>
      <div class="title">Recebido por método</div>
      <div class="row"><span class="k">Dinheiro</span><span class="v">R$ ${(bm.cash||0).toFixed(2)}</span></div>
      <div class="row"><span class="k">Cartão</span><span class="v">R$ ${(bm.card||0).toFixed(2)}</span></div>
      <div class="row"><span class="k">Pix</span><span class="v">R$ ${(bm.pix||0).toFixed(2)}</span></div>
      <div class="divider"></div>
      <div class="title">Gaveta (dinheiro)</div>
      <div class="row"><span class="k">Esperado</span><span class="v">R$ ${(c.expected_cash||0).toFixed(2)}</span></div>
      <div class="row"><span class="k">Contado</span><span class="v">R$ ${(c.counted_cash||0).toFixed(2)}</span></div>
      <div class="row"><span class="k">Sobra p/ amanhã</span><span class="v">R$ ${(c.leftover_cash||0).toFixed(2)}</span></div>
      <div class="diff"><div class="lbl">${diffLbl}</div><div class="val">${diff>0?"+":""}R$ ${diff.toFixed(2)}</div></div>
      ${mvLines ? `<div class="title">Movimentos</div>${mvLines}<div class="divider"></div>` : ""}
      ${c.notes ? `<div class="title">Observação</div><div class="sub">${c.notes}</div><div class="divider"></div>` : ""}
      <div class="sig">${c.closed_by_name}<br/>Responsável do caixa</div>
      <div class="sig">Supervisor / Admin</div>
      <div class="center sub" style="margin-top:10px">American Fit</div>
      <script>window.onload=()=>setTimeout(()=>{window.print();},250);</script>
    </body></html>`;
    const w = window.open("", "_blank", "width=380,height=800");
    if (!w) return toast.error("Popup bloqueado pelo navegador");
    w.document.write(html); w.document.close();
  };

  const reopenDay = async () => {
    if (needsUnitForAction) return toast.error("Selecione uma unidade específica no topo antes de reabrir o caixa");
    const reason = window.prompt("Motivo para reabrir o caixa (obrigatório — ficará no histórico de auditoria):", "");
    if (reason === null) return; // cancelou
    const r = (reason || "").trim();
    if (!r) return toast.error("Motivo obrigatório");
    try {
      const qObj = { date, reason: r };
      if (isAdmin && scopeUnit) qObj.unit_id = scopeUnit;
      const q = new URLSearchParams(qObj).toString();
      await api.post(`/finance/reopen-day?${q}`);
      toast.success("Caixa reaberto — motivo registrado na auditoria");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  if (!data) return <div className="p-8 text-slate-500">Carregando...</div>;
  const hidden = !!data.values_hidden;
  const money = (v) => hidden ? "Oculto" : `R$ ${(v || 0).toFixed(2)}`;

  return (
    <div className="space-y-4">
      <div className="print:hidden">
        <PageHeader
          title="Caixa do Dia"
          subtitle="Vendas de mensalidades, sangrias e suprimentos por dia"
          actions={
            <>
              <Input type="date" value={date} onChange={(e)=>setDate(e.target.value)} className="w-44" data-testid="daily-cash-date"/>
              {isAdmin && (
                <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                  <SelectTrigger className="w-56" data-testid="daily-cash-unit-select">
                    <Building2 className="w-4 h-4 mr-2 text-slate-500"/>
                    <SelectValue placeholder="Unidade"/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as unidades (agregado)</SelectItem>
                    {units.map(u => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
              {isAdmin && (
                <Button variant="outline" onClick={openReport} data-testid="cash-report-button" className="border-rose-600 text-rose-700 hover:bg-rose-50 font-bold uppercase"><FileText className="w-4 h-4 mr-2"/>Relatório</Button>
              )}
              {!data.closed ? (
                <>
                  <Button variant="outline" onClick={()=>openMv("suprimento")} data-testid="cash-suprimento-button" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-bold uppercase"><ArrowUp className="w-4 h-4 mr-1"/>Suprimento</Button>
                  <Button variant="outline" onClick={()=>openMv("sangria")} data-testid="cash-sangria-button" className="border-rose-600 text-rose-700 hover:bg-rose-50 font-bold uppercase"><ArrowDown className="w-4 h-4 mr-1"/>Sangria</Button>
                  {canClose && <Button onClick={openCloseDialog} data-testid="cash-close-day-button" className="bg-slate-900 hover:bg-black text-white font-bold uppercase"><Lock className="w-4 h-4 mr-2"/>Fechar caixa</Button>}
                </>
              ) : (
                <>
                  {!hidden && <>
                  <Button onClick={()=>window.print()} data-testid="cash-print-button" className="bg-rose-600 hover:bg-rose-700 font-bold uppercase"><Printer className="w-4 h-4 mr-2"/>Imprimir</Button>
                  <Button onClick={printThermalClosure} data-testid="cash-thermal-button" className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase"><Printer className="w-4 h-4 mr-2"/>Térmica 80mm</Button>
                  <Button onClick={printClosure} data-testid="cash-pdf-button" className="bg-slate-900 hover:bg-black text-white font-bold uppercase"><Printer className="w-4 h-4 mr-2"/>Comprovante PDF</Button>
                  <Button onClick={sendClosureWhatsApp} data-testid="cash-wa-button" className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase"><MessageCircle className="w-4 h-4 mr-2"/>Enviar ao Admin</Button>
                  </>}
                  {isAdmin && <Button variant="outline" onClick={reopenDay} data-testid="cash-reopen-button" className="font-bold uppercase">Reabrir</Button>}
                  {!isAdmin && canReopen && <Button variant="outline" onClick={reopenDay} data-testid="cash-reopen-button" className="font-bold uppercase">Reabrir</Button>}
                </>
              )}
            </>
          }
        />
      </div>

      {data && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="cash-opening-balance">
          <div className="text-sm text-emerald-900"><span className="font-bold uppercase text-xs tracking-wider">Saldo inicial</span>{data.opening_balance_from ? ` · deixado no fechamento de ${data.opening_balance_from.split("-").reverse().join("/")}` : " · nenhum fechamento anterior"}</div>
          <div className="font-mono font-black text-lg text-emerald-800">R$ {(data.opening_balance || 0).toFixed(2)}</div>
        </div>
      )}

      {data.closed && (
        <Card className="p-4 border-2 border-slate-900 bg-slate-900 text-white" data-testid="cash-closed-banner">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div><Lock className="w-5 h-5 inline mr-2"/><strong>CAIXA FECHADO</strong> em {new Date(data.closure.closed_at).toLocaleString("pt-BR")} por {data.closure.closed_by_name}</div>
            <div className="text-xs opacity-80" data-testid="cash-closed-total">{hidden ? "Valores visíveis apenas para o administrador" : `Total: R$ ${(data.closure.total || 0).toFixed(2)}`}</div>
          </div>
          {data.closure.counted_cash !== undefined && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-white/20 text-xs" data-testid="cash-closed-summary">
              <div><div className="opacity-70 uppercase text-[10px]">Esperado (dinheiro)</div><div className="font-heading font-bold text-base">R$ {(data.closure.expected_cash||0).toFixed(2)}</div></div>
              <div><div className="opacity-70 uppercase text-[10px]">Contado</div><div className="font-heading font-bold text-base">R$ {(data.closure.counted_cash||0).toFixed(2)}</div></div>
              <div><div className="opacity-70 uppercase text-[10px]">Sobra p/ amanhã</div><div className="font-heading font-bold text-base">R$ {(data.closure.leftover_cash||0).toFixed(2)}</div></div>
              <div><div className="opacity-70 uppercase text-[10px]">Diferença</div>
                <div className={`font-heading font-bold text-base ${Math.abs(data.closure.difference||0) < 0.01 ? "text-emerald-300" : (data.closure.difference||0) > 0 ? "text-sky-300" : "text-rose-300"}`}>
                  {(data.closure.difference||0) > 0 ? "+" : ""}R$ {(data.closure.difference||0).toFixed(2)}
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="p-4 bg-gradient-to-br from-rose-50 to-white border-rose-200" data-testid="daily-cash-total">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total líquido</div>
          <div className="font-heading text-2xl font-bold text-rose-600 mt-1">{money(data.total)}</div>
          <div className="text-xs text-slate-500">{hidden ? "—" : `${data.count} venda(s)`}</div>
        </Card>
        {["cash","card","pix"].map(m => {
          const M = METHOD_META[m]; const Icon = M.icon;
          return (
            <Card key={m} className="p-4 border" style={{ borderColor: M.color + "33", background: M.color + "08" }} data-testid={`daily-cash-${m}`}>
              <Icon className="w-4 h-4 mb-1" style={{ color: M.color }}/>
              <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: M.color }}>{M.label}</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">{money(data.by_method[m])}</div>
            </Card>
          );
        })}
        <Card className="p-4 border border-slate-200">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Movimentos</div>
          <div className="text-xs text-emerald-600 mt-1">{hidden ? "Oculto" : `+ R$ ${data.suprimento.toFixed(2)}`}</div>
          <div className="text-xs text-rose-600">{hidden ? "" : `- R$ ${data.sangria.toFixed(2)}`}</div>
        </Card>
      </div>

      {data.closed ? (
        adminPeek && isAdmin ? (
          <>
            <Card className="p-3 border border-amber-300 bg-amber-50 flex items-center justify-between flex-wrap gap-2" data-testid="admin-peek-banner">
              <div className="text-xs text-amber-900 font-semibold flex items-center gap-2">
                <Eye className="w-4 h-4"/>
                Modo auditoria do admin: vendas do caixa fechado visíveis apenas para você — nenhuma reabertura foi registrada.
              </div>
              <Button size="sm" variant="outline" onClick={()=>setAdminPeek(false)} className="border-amber-400 text-amber-800 hover:bg-amber-100 font-bold uppercase" data-testid="admin-peek-hide">
                <EyeOff className="w-4 h-4 mr-1"/>Ocultar novamente
              </Button>
            </Card>
            <Card className="border border-slate-200 overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Hora</th><th className="px-4 py-3">Descrição</th><th className="px-4 py-3">Método</th><th className="px-4 py-3">Vendedor</th><th className="px-4 py-3 text-right">Valor</th>
                  </tr>
                </thead>
                <tbody data-testid="daily-cash-sales-list-peek">
                  {data.sales.map(s => (
                    <tr key={s.session_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm">{new Date(s.created_at).toLocaleTimeString("pt-BR", {hour:'2-digit', minute:'2-digit'})}</td>
                      <td className="px-4 py-3"><div className="font-medium text-slate-900">{s.student_name}</div><div className="text-xs text-slate-500">{s.plan_name}</div></td>
                      <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full font-semibold" style={{background:(METHOD_META[s.method]?.color||"#94a3b8")+"1a", color: METHOD_META[s.method]?.color||"#475569"}}>{METHOD_META[s.method]?.label || s.method}</span></td>
                      <td className="px-4 py-3 text-sm text-slate-600">{s.sold_by_name}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-emerald-600">R$ {s.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                  {data.movements.map(m => (
                    <tr key={m.id} className="border-b border-slate-100 bg-slate-50/50">
                      <td className="px-4 py-3 text-sm">{new Date(m.created_at).toLocaleTimeString("pt-BR", {hour:'2-digit', minute:'2-digit'})}</td>
                      <td className="px-4 py-3"><div className="font-medium text-slate-900">{m.type === "sangria" ? "🔻 Sangria" : "🔺 Suprimento"}</div><div className="text-xs text-slate-500">{m.reason}</div></td>
                      <td className="px-4 py-3"><span className="text-xs text-slate-500">—</span></td>
                      <td className="px-4 py-3 text-sm text-slate-600">{m.created_by_name}</td>
                      <td className={`px-4 py-3 text-right font-mono font-bold ${m.type === "sangria" ? "text-rose-600" : "text-emerald-600"}`}>{m.type === "sangria" ? "-" : "+"} R$ {m.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                  {data.sales.length === 0 && data.movements.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">Nenhum movimento neste dia</td></tr>}
                </tbody>
              </table>
            </Card>
          </>
        ) : (
          <Card className="border-2 border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="daily-cash-closed-placeholder">
            <Lock className="w-10 h-10 text-slate-400 mx-auto mb-2"/>
            <div className="font-heading font-black uppercase text-slate-700">Detalhes das vendas ocultos</div>
            <p className="text-sm text-slate-500 mt-1">{hidden ? "O caixa deste dia está fechado. Os valores e as vendas ficam visíveis apenas para o administrador." : "O caixa deste dia está fechado. Reabra o caixa para visualizar nomes de alunos, movimentos e vendas."}</p>
            {isAdmin && (
              <Button onClick={()=>setAdminPeek(true)} className="mt-4 bg-slate-900 hover:bg-black text-white font-bold uppercase" data-testid="admin-peek-show">
                <Eye className="w-4 h-4 mr-2"/>Ver vendas (admin)
              </Button>
            )}
          </Card>
        )
      ) : (
      <Card className="border border-slate-200 overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Hora</th><th className="px-4 py-3">Descrição</th><th className="px-4 py-3">Método</th><th className="px-4 py-3">Vendedor</th><th className="px-4 py-3 text-right">Valor</th>
              {canDelete && !data.closed && <th className="px-4 py-3 text-right">Ações</th>}
            </tr>
          </thead>
          <tbody data-testid="daily-cash-sales-list">
            {data.sales.map(s => (
              <tr key={s.session_id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 text-sm">{new Date(s.created_at).toLocaleTimeString("pt-BR", {hour:'2-digit', minute:'2-digit'})}</td>
                <td className="px-4 py-3"><div className="font-medium text-slate-900">{s.student_name}</div><div className="text-xs text-slate-500">{s.plan_name}</div></td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full font-semibold" style={{background:(METHOD_META[s.method]?.color||"#94a3b8")+"1a", color: METHOD_META[s.method]?.color||"#475569"}}>{METHOD_META[s.method]?.label || s.method}</span></td>
                <td className="px-4 py-3 text-sm text-slate-600">{s.sold_by_name}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-emerald-600">R$ {s.amount.toFixed(2)}</td>
                {canDelete && !data.closed && (
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={()=>openDelete(s)} data-testid="daily-cash-delete-button" className="border-rose-300 text-rose-600 hover:bg-rose-50">
                      <Trash2 className="w-4 h-4 mr-1"/>Excluir
                    </Button>
                  </td>
                )}
              </tr>
            ))}
            {data.movements.map(m => (
              <tr key={m.id} className="border-b border-slate-100 bg-slate-50/50">
                <td className="px-4 py-3 text-sm">{new Date(m.created_at).toLocaleTimeString("pt-BR", {hour:'2-digit', minute:'2-digit'})}</td>
                <td className="px-4 py-3"><div className="font-medium text-slate-900">{m.type === "sangria" ? "🔻 Sangria" : "🔺 Suprimento"}</div><div className="text-xs text-slate-500">{m.reason}</div></td>
                <td className="px-4 py-3"><span className="text-xs text-slate-500">—</span></td>
                <td className="px-4 py-3 text-sm text-slate-600">{m.created_by_name}</td>
                <td className={`px-4 py-3 text-right font-mono font-bold ${m.type === "sangria" ? "text-rose-600" : "text-emerald-600"}`}>{m.type === "sangria" ? "-" : "+"} R$ {m.amount.toFixed(2)}</td>
                {canDelete && !data.closed && <td className="px-4 py-3"></td>}
              </tr>
            ))}
            {data.sales.length === 0 && data.movements.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Nenhum movimento neste dia</td></tr>}
          </tbody>
        </table>
      </Card>
      )}

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent data-testid="close-cash-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-slate-900"/>Fechar caixa do dia</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Total geral</span><span className="font-mono font-bold">R$ {(data?.total||0).toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-1"><span className="text-slate-500">Saldo inicial (fechamento anterior)</span><span className="font-mono" data-testid="close-opening-balance">R$ {(data?.opening_balance||0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Vendas em dinheiro</span><span className="font-mono">R$ {(data?.by_method?.cash||0).toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-1"><span className="text-emerald-700">Esperado em dinheiro</span><span className="font-mono font-bold text-emerald-700" data-testid="close-expected-cash">R$ {((data?.opening_balance||0) + (data?.by_method?.cash||0)).toFixed(2)}</span></div>
              <div className="text-xs text-slate-500 pt-1">Cartão/Pix/Stripe não entram na conferência de gaveta.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Contado na gaveta (R$)</Label>
                <Input type="number" step="0.01" value={closeForm.counted_cash} onChange={(e)=>setCloseForm({...closeForm, counted_cash: e.target.value})} data-testid="close-counted-input"/>
              </div>
              <div>
                <Label>Deixar de saldo (R$)</Label>
                <Input type="number" step="0.01" value={closeForm.leftover_cash} onChange={(e)=>setCloseForm({...closeForm, leftover_cash: e.target.value})} placeholder="troco p/ amanhã" data-testid="close-leftover-input"/>
              </div>
            </div>
            {(() => {
              const c = parseFloat(closeForm.counted_cash || 0);
              const e = (data?.opening_balance || 0) + (data?.by_method?.cash || 0);
              const d = c - e;
              const ok = Math.abs(d) < 0.01;
              return (
                <div className={`rounded-lg border-2 p-3 text-sm ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : d > 0 ? "border-sky-300 bg-sky-50 text-sky-900" : "border-rose-300 bg-rose-50 text-rose-900"}`} data-testid="close-diff-preview">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{ok ? "✓ Confere certinho" : d > 0 ? "Sobra na gaveta" : "Falta na gaveta"}</span>
                    <span className="font-heading font-bold text-lg">{d > 0 ? "+" : ""}R$ {d.toFixed(2)}</span>
                  </div>
                </div>
              );
            })()}
            <div>
              <Label>Observação (opcional)</Label>
              <Textarea rows={2} value={closeForm.notes} onChange={(e)=>setCloseForm({...closeForm, notes: e.target.value})} placeholder="Ex: passei R$ 10 como troco emprestado ao vizinho"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={()=>setCloseOpen(false)}>Cancelar</Button>
              <Button onClick={closeDay} disabled={closing} className="bg-slate-900 hover:bg-black text-white font-bold uppercase" data-testid="close-confirm"><Lock className="w-4 h-4 mr-2"/>{closing ? "Fechando..." : "Confirmar fechamento"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent data-testid="delete-sale-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-rose-600"><AlertTriangle className="w-5 h-5"/>Excluir venda do caixa</DialogTitle></DialogHeader>
          {delSale && (
            <div className="space-y-4 mt-2">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="font-semibold text-slate-900">{delSale.student_name}</div>
                <div className="text-slate-600">{delSale.plan_name} • {METHOD_META[delSale.method]?.label || delSale.method}</div>
                <div className="text-emerald-700 font-mono font-bold mt-1">R$ {delSale.amount.toFixed(2)}</div>
              </div>
              <div>
                <Label>Motivo (obrigatório)</Label>
                <Textarea rows={2} value={delReason} onChange={(e)=>setDelReason(e.target.value)} placeholder="Ex: cobrança duplicada, cliente desistiu, erro de lançamento" data-testid="delete-sale-reason"/>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                Apenas os dias <strong>deste plano</strong> serão revertidos do acesso do aluno. Se ele tiver outro plano ativo (comprado antes ou depois), esse plano continua valendo normalmente.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={()=>setDelOpen(false)} data-testid="delete-sale-cancel">Cancelar</Button>
                <Button onClick={confirmDelete} disabled={delLoading} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-sale-confirm"><Trash2 className="w-4 h-4 mr-1"/>{delLoading ? "Excluindo..." : "Excluir venda"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={mvOpen} onOpenChange={setMvOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{mvType === "sangria" ? "Registrar Sangria (saída)" : "Registrar Suprimento (entrada)"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <div><Label>Valor R$</Label><Input type="number" step="0.01" value={mvForm.amount} onChange={(e)=>setMvForm({...mvForm,amount:e.target.value})} data-testid="cash-movement-amount"/></div>
            <div><Label>Motivo (obrigatório)</Label><Textarea rows={2} value={mvForm.reason} onChange={(e)=>setMvForm({...mvForm,reason:e.target.value})} placeholder={mvType === "sangria" ? "Ex: retirada para depósito bancário" : "Ex: troco adicional inicial"} data-testid="cash-movement-reason"/></div>
            <Button onClick={saveMv} data-testid="cash-movement-save" className={mvType === "sangria" ? "w-full bg-rose-600 hover:bg-rose-700" : "w-full bg-emerald-600 hover:bg-emerald-700"}>Confirmar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="closures-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-rose-600"/>Relatório de Fechamentos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-slate-50 rounded-xl">
              <div>
                <Label className="text-xs">Período</Label>
                <Select value={reportPeriod} onValueChange={setReportPeriod}>
                  <SelectTrigger data-testid="report-period"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Diário</SelectItem>
                    <SelectItem value="month">Mensal</SelectItem>
                    <SelectItem value="year">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Data de referência</Label>
                <Input type="date" value={reportDate} onChange={(e)=>setReportDate(e.target.value)} data-testid="report-date"/>
              </div>
              <div className="flex items-end gap-2">
                <Button variant="outline" onClick={exportReportCSV} className="flex-1" data-testid="report-csv"><Download className="w-4 h-4 mr-2"/>CSV</Button>
                <Button onClick={printReport} className="flex-1 bg-slate-900 hover:bg-black text-white" data-testid="report-pdf"><Printer className="w-4 h-4 mr-2"/>PDF</Button>
              </div>
            </div>

            {reportLoading && <div className="py-10 text-center text-slate-400 text-sm">Carregando…</div>}

            {reportData && !reportLoading && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="p-3 border bg-rose-50 border-rose-200">
                    <div className="text-[10px] uppercase tracking-widest text-rose-700 font-bold">Total vendido</div>
                    <div className="font-heading text-2xl font-black text-rose-700 mt-1">R$ {reportData.totals.total_sales.toFixed(2)}</div>
                    <div className="text-xs text-slate-500">{reportData.totals.closures_count} fechamento(s)</div>
                  </Card>
                  <Card className="p-3 border bg-emerald-50 border-emerald-200">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Dinheiro</div>
                    <div className="font-heading text-2xl font-black text-emerald-700 mt-1">R$ {reportData.totals.cash.toFixed(2)}</div>
                  </Card>
                  <Card className="p-3 border bg-sky-50 border-sky-200">
                    <div className="text-[10px] uppercase tracking-widest text-sky-700 font-bold">Cartão</div>
                    <div className="font-heading text-2xl font-black text-sky-700 mt-1">R$ {reportData.totals.card.toFixed(2)}</div>
                  </Card>
                  <Card className="p-3 border bg-rose-50 border-rose-200">
                    <div className="text-[10px] uppercase tracking-widest text-rose-700 font-bold">Pix</div>
                    <div className="font-heading text-2xl font-black text-rose-700 mt-1">R$ {reportData.totals.pix.toFixed(2)}</div>
                  </Card>
                </div>

                {reportData.breakdown.length > 0 && (
                  <Card className="p-3 border border-slate-200">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
                      {reportData.period === "month" ? "Por dia" : "Por mês"}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50"><tr className="text-xs uppercase tracking-wider text-slate-500">
                          <th className="px-2 py-2 text-left">Período</th>
                          <th className="px-2 py-2 text-right">Dinheiro</th>
                          <th className="px-2 py-2 text-right">Cartão</th>
                          <th className="px-2 py-2 text-right">Pix</th>
                          <th className="px-2 py-2 text-right">Total</th>
                          <th className="px-2 py-2 text-center">Fechamentos</th>
                        </tr></thead>
                        <tbody>
                          {reportData.breakdown.map(b => (
                            <tr key={b.label} className="border-t border-slate-100">
                              <td className="px-2 py-1.5 font-mono">{b.label}</td>
                              <td className="px-2 py-1.5 text-right">R$ {b.cash.toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-right">R$ {b.card.toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-right">R$ {b.pix.toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-right font-bold">R$ {b.total.toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-center">{b.closures}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}

                <Card className="p-0 border border-slate-200 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-widest text-slate-500 font-bold">
                    Fechamentos ({reportData.closures.length})
                  </div>
                  {reportData.closures.length === 0 ? (
                    <div className="p-6 text-center text-slate-400 text-sm">Nenhum fechamento no período</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="report-closures-table">
                        <thead className="bg-slate-50"><tr className="uppercase tracking-wider text-slate-500">
                          <th className="px-2 py-2 text-left">Data</th>
                          <th className="px-2 py-2 text-left">Unidade</th>
                          <th className="px-2 py-2 text-left">Por</th>
                          <th className="px-2 py-2 text-right">Dinheiro</th>
                          <th className="px-2 py-2 text-right">Cartão</th>
                          <th className="px-2 py-2 text-right">Pix</th>
                          <th className="px-2 py-2 text-right">Total</th>
                          <th className="px-2 py-2 text-right">Diferença</th>
                        </tr></thead>
                        <tbody>
                          {reportData.closures.map(c => (
                            <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                              <td className="px-2 py-1.5 font-mono">{c.date}</td>
                              <td className="px-2 py-1.5">{c.unit_name}</td>
                              <td className="px-2 py-1.5 truncate max-w-[140px]">{c.closed_by_name}</td>
                              <td className="px-2 py-1.5 text-right">R$ {c.cash.toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-right">R$ {c.card.toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-right">R$ {c.pix.toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-right font-bold">R$ {(c.total||0).toFixed(2)}</td>
                              <td className={`px-2 py-1.5 text-right ${Math.abs(c.difference||0)<0.01?'text-slate-500':(c.difference||0)>0?'text-sky-700':'text-rose-700'}`}>
                                {(c.difference||0)>0?"+":""}R$ {(c.difference||0).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
